import { useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronUp,
  faCircleInfo,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons'
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons'
import {
  FundingTemplateFieldOverride,
  FundingTemplateFormField,
} from '../../api/fundingApi'
import {
  SearchableCheckboxOption,
  SearchableCheckboxSelect,
} from './SearchableCheckboxSelect'
import {
  FieldOverrideState,
  resolveTemplateFieldValue,
} from './validateTemplateFields'
import { useStyles } from './DynamicSchemaFields.styles'

export type { FieldOverrideState }

const AWS_ACCOUNT_HELPER =
  "AWS account to receive approved credits or cash. They can't be transferred or reversed."

const ALL_APPROVED_US_STATES = 'All Approved US States'
const SEARCHABLE_ENUM_MIN = 8

interface DynamicSchemaFieldsProps {
  fields: FundingTemplateFormField[]
  overrides: FieldOverrideState
  globalDefaults?: Record<string, string | null> | null
  globalsConfigured?: boolean
  onChange: (path: string, next: FundingTemplateFieldOverride) => void
  simpleValues?: boolean
  values?: Record<string, string | null>
  onValueChange?: (path: string, value: string) => void
  layout?: 'rows' | 'grid'
  /** When false with layout=grid, render one flat field grid (no group accordions). */
  groupFields?: boolean
  disabled?: boolean
  fieldErrors?: Record<string, string>
  /** Paths the user may remove (user-added fields, not static defaults). */
  removablePaths?: ReadonlySet<string>
  onRemoveField?: (path: string) => void
}

const humanize = (label: string) =>
  label
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()

const humanizeGroup = (group: string) =>
  group
    .split('.')
    .map(part => humanize(part))
    .filter(Boolean)
    .join(' · ')

const optionLabel = (field: FundingTemplateFormField, option: string) =>
  field.option_labels?.[option] ?? option

const leafPath = (path: string) => {
  const parts = path.split('.')
  return parts[parts.length - 1] ?? path
}

const isCountryLeaf = (leaf: string) => /Country$/i.test(leaf)
const isStateLeaf = (leaf: string) => /State$/i.test(leaf)
const isCurrencyLeaf = (leaf: string) => /Currency$/i.test(leaf)
const isDateLeaf = (leaf: string) =>
  /Date$/i.test(leaf) || leaf === 'StartDate' || leaf === 'EndDate'

/** Common ISO 4217 codes when schema omits a Currency enum (ACE / cash claims). */
const CURRENCY_ENUM_FALLBACK = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'JPY',
  'INR',
  'CNY',
  'SGD',
  'BRL',
  'MXN',
  'CHF',
  'SEK',
  'NOK',
  'DKK',
  'NZD',
  'HKD',
  'KRW',
  'ZAR',
  'AED',
]

/** Schema paths that duplicate Reusable resources → Pricing calculator URL. */
export const isPricingCalculatorSchemaPath = (path: string) => {
  const p = path.toLowerCase()
  return p.includes('calculator') && p.includes('url')
}

const GENERIC_LEAVES = new Set([
  'Amount',
  'Currency',
  'StartDate',
  'EndDate',
  'Description',
])

const isTechnicalDescription = (text: string) => {
  const t = text.trim().toLowerCase()
  if (!t) return true
  return (
    t.startsWith('amount as string') ||
    t.startsWith('iso 8601') ||
    t.startsWith('example:') ||
    t.includes('must be greater than') ||
    t.includes('formatted datetime') ||
    t.includes('alphanumeric characters')
  )
}

/** Prefer human schema help; fall back to a short tip for Currency. */
export const schemaFieldTooltip = (field: FundingTemplateFormField) => {
  const raw = field.description?.trim()
  if (raw && !isTechnicalDescription(raw)) return raw
  const leaf = leafPath(field.path)
  if (isCurrencyLeaf(leaf)) {
    return 'ISO 4217 currency code (for example USD or EUR).'
  }
  if (raw) return raw
  return null
}

/** Humanized path for generic leaves (Amount/Currency/…) — avoids duplicate labels. */
export const schemaFieldDisplayLabel = (field: FundingTemplateFormField) => {
  const leaf = leafPath(field.path)
  const base = humanize(field.label?.trim() || leaf)
  const parts = field.path.split('.').filter(part => !/^\d+$/.test(part))
  if (!GENERIC_LEAVES.has(leaf)) {
    return base
  }
  if (parts.length < 2) return base
  const context = parts
    .slice(0, -1)
    .map(part => humanize(part))
    .filter(Boolean)
  return context.length > 0 ? `${context.join(' · ')} · ${base}` : base
}

const displayLabelFor = (field: FundingTemplateFormField) =>
  schemaFieldDisplayLabel(field)

const helperFor = (field: FundingTemplateFormField, leaf: string) => {
  if (leaf === 'AwsAccountId') return AWS_ACCOUNT_HELPER
  const raw = field.description?.trim()
  if (!raw || isTechnicalDescription(raw)) return null
  return raw
}

const parseMulti = (raw: string | null | undefined) =>
  (raw || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)

const resolveDisplayValue = ({
  field,
  overrides,
  globalDefaults,
  globalsConfigured,
  simpleValues,
  values,
}: {
  field: FundingTemplateFormField
  overrides: FieldOverrideState
  globalDefaults?: Record<string, string | null> | null
  globalsConfigured: boolean
  simpleValues: boolean
  values?: Record<string, string | null>
}) => {
  if (simpleValues) return values?.[field.path] ?? ''
  return resolveTemplateFieldValue(
    field,
    overrides,
    globalDefaults,
    globalsConfigured
  )
}

interface SchemaFieldProps {
  field: FundingTemplateFormField
  groupName: string
  displayValue: string
  locked: boolean
  error?: string
  layout: 'rows' | 'grid'
  enumValues: string[]
  enumLabels: Record<string, string> | null | undefined
  hideState: boolean
  canRemove: boolean
  onRemoveField?: (path: string) => void
  onValue: (next: string) => void
  classes: ReturnType<typeof useStyles>['classes']
  cx: ReturnType<typeof useStyles>['cx']
}

const SchemaField = ({
  field,
  displayValue,
  locked,
  error,
  layout,
  enumValues,
  enumLabels,
  hideState,
  canRemove,
  onRemoveField,
  onValue,
  classes,
  cx,
}: SchemaFieldProps) => {
  const [focused, setFocused] = useState(false)
  const label = displayLabelFor(field)
  const leaf = leafPath(field.path)
  const isBool = field.type === 'boolean'
  const isAws = leaf === 'AwsAccountId'
  const isState = isStateLeaf(leaf)
  const isMultiEnum = field.type === 'array' || isState
  const helper = helperFor(field, leaf)
  const tooltip = schemaFieldTooltip(field)
  const isDate =
    isDateLeaf(leaf) || field.type === 'date' || field.type === 'datetime'
  const hasValue = Boolean(displayValue)
  // Date inputs always show browser chrome — keep the float label at the top
  // so it never sits on top of the native date text.
  const floated = focused || hasValue || isDate
  const labeledOption = (option: string) =>
    enumLabels?.[option] ?? optionLabel(field, option)

  const useSearchable =
    layout === 'grid' &&
    enumValues.length > 0 &&
    (isCountryLeaf(leaf) ||
      isState ||
      isCurrencyLeaf(leaf) ||
      enumValues.length > SEARCHABLE_ENUM_MIN ||
      field.type === 'array')

  if (hideState) return null

  const removeBtn = canRemove ? (
    <button
      type="button"
      className={classes.removeFieldButton}
      aria-label={`Remove ${label}`}
      data-testid={`remove-field-${field.path}`}
      onClick={event => {
        event.stopPropagation()
        onRemoveField?.(field.path)
      }}
    >
      <FontAwesomeIcon icon={faTrashCan} />
    </button>
  ) : null

  const infoBtn = tooltip ? (
    <Tooltip title={tooltip} arrow placement="top" enterDelay={300}>
      <button
        type="button"
        className={classes.fieldInfoButton}
        aria-label="Show field description"
        data-testid={`field-info-${field.path}`}
        onClick={event => event.preventDefault()}
      >
        <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
      </button>
    </Tooltip>
  ) : null

  const normalizedBool = displayValue.trim().toLowerCase()
  const isBoolYes =
    normalizedBool === 'true' || normalizedBool === 'yes'
  const isBoolNo =
    normalizedBool === 'false' || normalizedBool === 'no'

  const boolRadios = isBool ? (
    <div className={classes.radioRow} role="radiogroup" aria-label={label}>
      <label className={classes.radioOption}>
        <input
          type="radio"
          name={`bool-${field.path}`}
          aria-label={`${label} Yes`}
          checked={isBoolYes}
          disabled={locked}
          onChange={() => onValue('true')}
        />
        Yes
      </label>
      <label className={classes.radioOption}>
        <input
          type="radio"
          name={`bool-${field.path}`}
          aria-label={`${label} No`}
          checked={isBoolNo}
          disabled={locked}
          onChange={() => onValue('false')}
        />
        No
      </label>
    </div>
  ) : null

  if (layout === 'grid' && isBool) {
    // Always keep the label on the top notch so Yes/No radios never overlap it.
    return (
      <div key={field.path} className={classes.gridField}>
        <div
          className={cx(
            classes.floatField,
            classes.floatFieldFloated,
            classes.boolFloatField,
            error && classes.floatFieldError
          )}
        >
          <Typography
            component="span"
            className={cx(classes.floatLabelInField, classes.floatLabelFloated)}
          >
            {label}
          </Typography>
          {boolRadios}
          {infoBtn || removeBtn ? (
            <span className={classes.fieldTrailing}>
              {infoBtn}
              {removeBtn}
            </span>
          ) : null}
        </div>
        {helper ? (
          <Typography component="p" className={classes.helperText}>
            {helper}
          </Typography>
        ) : null}
        {error ? (
          <Typography component="p" className={classes.errorText}>
            {error}
          </Typography>
        ) : null}
      </div>
    )
  }

  if (useSearchable) {
    const options: SearchableCheckboxOption[] = enumValues.map(value => ({
      value,
      label: labeledOption(value),
    }))
    const selected = isMultiEnum
      ? parseMulti(displayValue)
      : displayValue
        ? [displayValue]
        : []

    return (
      <div key={field.path} className={classes.gridField}>
        <SearchableCheckboxSelect
          id={`template-field-${field.path}`}
          label={label}
          options={options}
          value={selected}
          maxSelections={isMultiEnum ? undefined : 1}
          floatingLabel
          disabled={locked}
          placeholder=""
          endAction={
            infoBtn || removeBtn ? (
              <span className={classes.fieldTrailing}>
                {infoBtn}
                {removeBtn}
              </span>
            ) : null
          }
          onChange={next => {
            if (locked) return
            if (!isMultiEnum) {
              onValue(next[0] ?? '')
              return
            }
            let states = next
            if (next.includes(ALL_APPROVED_US_STATES)) {
              states = [ALL_APPROVED_US_STATES]
            } else {
              states = next.filter(v => v !== ALL_APPROVED_US_STATES)
            }
            onValue(states.join(','))
          }}
        />
        {helper ? (
          <Typography component="p" className={classes.helperText}>
            {helper}
          </Typography>
        ) : null}
        {error ? (
          <Typography component="p" className={classes.errorText}>
            {error}
          </Typography>
        ) : null}
      </div>
    )
  }

  const control =
    enumValues.length > 0 ? (
      <select
        className={layout === 'grid' ? classes.outlinedSelect : classes.select}
        aria-label={label}
        disabled={locked}
        value={displayValue}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={event => onValue(event.target.value)}
      >
        <option value=""> </option>
        {enumValues.map(option => (
          <option key={option} value={option}>
            {labeledOption(option)}
          </option>
        ))}
      </select>
    ) : isBool ? (
      boolRadios
    ) : (
      <input
        className={cx(
          layout === 'grid' ? classes.outlinedInput : classes.input,
          isDate && layout === 'grid' && classes.dateInput
        )}
        aria-label={label}
        disabled={locked}
        type={isDate ? 'date' : 'text'}
        maxLength={
          isAws ? 12 : field.max_length ?? undefined
        }
        value={displayValue}
        inputMode={
          field.type === 'number' || field.type === 'integer'
            ? 'numeric'
            : undefined
        }
        pattern={
          field.type === 'number' || field.type === 'integer'
            ? '[0-9]*'
            : undefined
        }
        autoComplete={isAws ? 'off' : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={event => {
          // AWS account ID allows letters (validated below). Other numeric
          // schema fields still block non-digits at the keyboard.
          const numericOnly =
            !isAws &&
            (field.type === 'number' || field.type === 'integer')
          if (!numericOnly) return
          if (
            event.ctrlKey ||
            event.metaKey ||
            event.altKey ||
            event.key === 'Backspace' ||
            event.key === 'Delete' ||
            event.key === 'Tab' ||
            event.key === 'Enter' ||
            event.key === 'ArrowLeft' ||
            event.key === 'ArrowRight' ||
            event.key === 'Home' ||
            event.key === 'End' ||
            (field.type === 'number' &&
              (event.key === '.' || event.key === '-'))
          ) {
            return
          }
          if (!/^\d$/.test(event.key)) {
            event.preventDefault()
          }
        }}
        onPaste={event => {
          if (isAws) return
          const numericOnly =
            field.type === 'number' || field.type === 'integer'
          if (!numericOnly) return
          event.preventDefault()
          const pasted = event.clipboardData.getData('text') || ''
          let next = `${displayValue}${pasted.replace(/\D/g, '')}`
          if (field.max_length != null) next = next.slice(0, field.max_length)
          onValue(next)
        }}
        onChange={event => {
          let next = event.target.value
          if (isAws) {
            // Keep letters so validation can show; still cap at 12 chars.
            next = next.replace(/[\r\n]/g, '').slice(0, 12)
          } else if (field.type === 'number' || field.type === 'integer') {
            next = next.replace(/[^\d.-]/g, '')
            if (field.type === 'integer') next = next.replace(/[^\d-]/g, '')
          }
          onValue(next)
        }}
      />
    )

  if (layout === 'grid') {
    const isSelect = enumValues.length > 0
    const awsTrimmed = isAws ? displayValue.trim() : ''
    const awsInlineError =
      isAws && awsTrimmed
        ? !/^\d+$/.test(awsTrimmed)
          ? 'AWS account ID must contain only numbers.'
          : awsTrimmed.length !== 12
            ? 'AWS account ID must be exactly 12 digits.'
            : ''
        : ''
    // Prefer schema/save error; otherwise show inline AWS feedback in the
    // helper slot so we do not add an extra line under the field.
    const feedbackError = error || awsInlineError
    const feedbackHelper =
      helper || (isAws ? AWS_ACCOUNT_HELPER : '')
    return (
      <div key={field.path} className={classes.gridField}>
        <div
          className={cx(
            classes.floatField,
            floated && classes.floatFieldFloated,
            Boolean(feedbackError) && classes.floatFieldError
          )}
        >
          <Typography
            component="span"
            className={cx(
              classes.floatLabelInField,
              floated && classes.floatLabelFloated
            )}
          >
            {label}
          </Typography>
          {control}
          {infoBtn || removeBtn || isSelect ? (
            <span className={classes.fieldTrailing}>
              {infoBtn}
              {removeBtn}
              {isSelect ? (
                <span className={classes.selectTrailing} aria-hidden>
                  <span className={classes.fieldDivider} />
                  <FontAwesomeIcon
                    icon={faCaretDown}
                    className={classes.caretIcon}
                  />
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
        {feedbackError || feedbackHelper ? (
          <Typography
            component="p"
            className={feedbackError ? classes.errorText : classes.helperText}
          >
            {feedbackError || feedbackHelper}
          </Typography>
        ) : null}
      </div>
    )
  }

  return (
    <div key={field.path} className={classes.row}>
      <div className={classes.labelCol}>
        <Typography component="span" className={classes.label}>
          {label}
        </Typography>
        {field.group && (
          <Typography component="span" className={classes.group}>
            {humanize(field.group)}
          </Typography>
        )}
      </div>
      <div className={classes.controlCol}>
        {control}
        {error ? (
          <Typography component="p" className={classes.errorText}>
            {error}
          </Typography>
        ) : null}
      </div>
    </div>
  )
}

export const DynamicSchemaFields = ({
  fields,
  overrides,
  globalDefaults,
  globalsConfigured = false,
  onChange,
  simpleValues = false,
  values,
  onValueChange,
  layout = 'rows',
  groupFields = true,
  disabled = false,
  fieldErrors = {},
  removablePaths,
  onRemoveField,
}: DynamicSchemaFieldsProps) => {
  const { classes, cx } = useStyles()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  )

  const groupKeyFor = (field: FundingTemplateFormField) => {
    const parts = field.path.split('.').filter(part => !/^\d+$/.test(part))
    if (parts.length >= 2) return parts.slice(0, -1).join('.')
    return field.group?.trim() || 'General'
  }

  const countryEnumFallback = useMemo(() => {
    return (
      fields.find(
        f => isCountryLeaf(leafPath(f.path)) && (f.enum?.length ?? 0) > 0
      ) ?? null
    )
  }, [fields])

  const geoSortRank = (field: FundingTemplateFormField) => {
    const leaf = leafPath(field.path)
    if (isCountryLeaf(leaf)) return 0
    if (isStateLeaf(leaf)) return 1
    return 2
  }

  const grouped = useMemo(() => {
    const map = new Map<string, FundingTemplateFormField[]>()
    for (const field of fields) {
      const key = groupKeyFor(field)
      const list = map.get(key) ?? []
      list.push(field)
      map.set(key, list)
    }
    return [...map.entries()].map(([key, list]) => [
      key,
      [...list].sort((a, b) => geoSortRank(a) - geoSortRank(b)),
    ]) as [string, FundingTemplateFormField[]][]
  }, [fields])

  /**
   * Flat Basic information grid:
   * - Static/default fields: catalog order with Country before State per group
   * - User-added (removable) fields: always last, in add order
   */
  const flatOrderedFields = useMemo(() => {
    const removable = removablePaths ?? new Set<string>()
    const fixed: FundingTemplateFormField[] = []
    const added: FundingTemplateFormField[] = []
    for (const field of fields) {
      if (removable.has(field.path)) added.push(field)
      else fixed.push(field)
    }
    const byGroup = new Map<string, FundingTemplateFormField[]>()
    const groupOrder: string[] = []
    for (const field of fixed) {
      const key = groupKeyFor(field)
      if (!byGroup.has(key)) {
        byGroup.set(key, [])
        groupOrder.push(key)
      }
      byGroup.get(key)!.push(field)
    }
    const out: FundingTemplateFormField[] = []
    for (const key of groupOrder) {
      const list = byGroup.get(key) ?? []
      out.push(...[...list].sort((a, b) => geoSortRank(a) - geoSortRank(b)))
    }
    return [...out, ...added]
  }, [fields, removablePaths])

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }))
  }

  const countryByGroup = useMemo(() => {
    const map = new Map<string, string>()
    for (const [groupName, groupFieldsList] of grouped) {
      const countryField = groupFieldsList.find(f =>
        isCountryLeaf(leafPath(f.path))
      )
      if (!countryField) continue
      const raw = resolveDisplayValue({
        field: countryField,
        overrides,
        globalDefaults,
        globalsConfigured,
        simpleValues,
        values,
      })
      map.set(groupName, String(raw ?? '').trim())
    }
    return map
  }, [
    grouped,
    overrides,
    globalDefaults,
    globalsConfigured,
    simpleValues,
    values,
  ])

  if (fields.length === 0) {
    return (
      <Typography component="p" className={classes.empty}>
        No configurable fields for this funding type.
      </Typography>
    )
  }

  const renderField = (
    field: FundingTemplateFormField,
    groupName: string
  ) => {
    const displayValue = String(
      resolveDisplayValue({
        field,
        overrides,
        globalDefaults,
        globalsConfigured,
        simpleValues,
        values,
      }) ?? ''
    )
    const locked = Boolean(disabled)
    const leaf = leafPath(field.path)
    const isCountry = isCountryLeaf(leaf)
    const isState = isStateLeaf(leaf)
    let enumValues =
      field.enum && field.enum.length > 0
        ? field.enum
        : isCountry && countryEnumFallback?.enum?.length
          ? countryEnumFallback.enum
          : isCurrencyLeaf(leaf)
            ? CURRENCY_ENUM_FALLBACK
            : []
    const enumLabels =
      field.option_labels && Object.keys(field.option_labels).length > 0
        ? field.option_labels
        : isCountry
          ? countryEnumFallback?.option_labels
          : null

    // Keep a saved currency code selectable even if not in the fallback list.
    if (
      isCurrencyLeaf(leaf) &&
      displayValue &&
      !enumValues.includes(displayValue)
    ) {
      enumValues = [displayValue, ...enumValues]
    }

    const hasCountrySibling = grouped
      .find(([name]) => name === groupName)?.[1]
      ?.some(f => isCountryLeaf(leafPath(f.path)))
    const hideState =
      layout === 'grid' &&
      isState &&
      Boolean(hasCountrySibling) &&
      countryByGroup.get(groupName) !== 'US'

    const onValue = (next: string) => {
      if (simpleValues) {
        onValueChange?.(field.path, next)
        return
      }
      onChange(field.path, { mode: 'override', value: next })
    }

    return (
      <SchemaField
        key={field.path}
        field={field}
        groupName={groupName}
        displayValue={displayValue}
        locked={locked}
        error={fieldErrors[field.path]}
        layout={layout}
        enumValues={enumValues}
        enumLabels={enumLabels}
        hideState={hideState}
        canRemove={
          Boolean(removablePaths?.has(field.path)) &&
          Boolean(onRemoveField) &&
          !locked
        }
        onRemoveField={onRemoveField}
        onValue={onValue}
        classes={classes}
        cx={cx}
      />
    )
  }

  if (layout === 'grid') {
    if (!groupFields) {
      return (
        <div
          className={classes.gridRoot}
          data-testid="dynamic-schema-fields"
          data-layout="grid"
          data-grouped="false"
        >
          <div className={classes.gridFields}>
            {flatOrderedFields.map(field =>
              renderField(field, groupKeyFor(field))
            )}
          </div>
        </div>
      )
    }
    return (
      <div
        className={classes.gridRoot}
        data-testid="dynamic-schema-fields"
        data-layout="grid"
        data-grouped="true"
      >
        {grouped.map(([groupName, groupFieldsList]) => {
          const expanded = Boolean(expandedGroups[groupName])
          const title = humanizeGroup(groupName)
          return (
            <div
              key={groupName}
              className={classes.groupBlock}
              data-testid={`schema-group-${groupName}`}
            >
              <div className={classes.groupHeader}>
                <Typography component="h3" className={classes.groupTitle}>
                  {title}
                </Typography>
                <button
                  type="button"
                  className={classes.groupCollapseButton}
                  aria-expanded={expanded}
                  aria-label={
                    expanded ? `Collapse ${title}` : `Expand ${title}`
                  }
                  onClick={() => toggleGroup(groupName)}
                >
                  <FontAwesomeIcon
                    icon={expanded ? faChevronUp : faChevronDown}
                  />
                </button>
              </div>
              {expanded ? (
                <div className={classes.gridFields}>
                  {groupFieldsList.map(field => renderField(field, groupName))}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div
      className={classes.root}
      data-testid="dynamic-schema-fields"
      data-layout="rows"
    >
      {fields.map(field =>
        renderField(field, field.group?.trim() || 'General')
      )}
    </div>
  )
}

export default DynamicSchemaFields
